<script setup>
import { computed } from 'vue';
import DOMPurify from 'dompurify';
import { useTranslation } from 'i18next-vue';
import { BRANDING } from '@/branding';
import { Plus, FolderOpen, Image, Video, Tag } from 'lucide-vue-next';

const { t } = useTranslation();

const props = defineProps({
    results: {
        type: Array,
        required: true,
    },
    searchTerm: {
        type: String,
        default: '',
    },
    maxHeight: {
        type: String,
        default: '400px', // Default max height
    },
    // Add highlightedIndex prop
    highlightedIndex: {
        type: Number,
        default: -1,
    },
    hasMoreResults: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits(['select-item', 'show-more']);

const selectItem = (item) => {
    emit('select-item', item);
};

// Helper to highlight the search term
// SYN-2736: o nome vem do usuário e vai para v-html — escapa o texto SEMPRE e só
// o <mark> que montamos aqui é markup. / The name is user-controlled and goes to
// v-html: always escape it; the only markup is the <mark> built here.
const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const highlightedName = (name) => {
    if (!name) {
        return name;
    }
    if (!props.searchTerm) {
        return escapeHtml(name);
    }
    // Escape special characters in the search term for regex
    const regex = new RegExp(`(${props.searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi');
    // split() com grupo de captura: índices ímpares são os trechos que casaram.
    // Split with a capture group: odd indexes are the matched parts.
    const html = name.split(regex)
        .map((part, i) => (i % 2 ? `<mark class="search-highlight">${escapeHtml(part)}</mark>` : escapeHtml(part)))
        .join('');
    return DOMPurify.sanitize(html);
};

const getItemIconComponent = (item) => {
    if (item.type === 'folder') return FolderOpen;
    if (item.type === 'experiment') {
        return item.media_type === 'video' ? Video : Image;
    }
    // Tags hidden for now
    // if (item.type === 'tag') return Tag;
    return null;
};

const getItemPath = (item) => {
    // Tags don't have a hierarchical path for this display.
    if (item.type === 'tag') {
        return null;
    }

    if (item.logical_path) {
        // 1. Get the parent path (remove the item's own name from the end).
        // O backend monta `logical_path` juntando nomes com "/" e não manda os
        // segmentos; um nome COM "/" ("Q1/Q2") quebrava o `lastIndexOf` e o
        // caminho mostrava parte do próprio nome. Tira-se o sufixo pelo NOME do
        // item quando ele bate (SYN-2738, bug do E2E); `lastIndexOf` fica só de
        // fallback para payloads sem `name`.
        let path = item.logical_path;
        const ownSuffix = item.name ? `/${item.name}` : null;
        const lastSlash = path.lastIndexOf('/');

        if (ownSuffix && path.endsWith(ownSuffix)) {
            path = path.slice(0, -ownSuffix.length);
        } else if (lastSlash !== -1) {
            path = path.substring(0, lastSlash);
        } else {
            // If no slash, it's likely just the root name itself, so parent is effectively root
            path = ""; 
        }

        // 2. Remove the product name prefix ("SynapSee Studio")
        const prefix = BRANDING.PRODUCT_NAME;
        if (path.startsWith(prefix)) {
            path = path.substring(prefix.length);
        }

        // 3. Clean up leading slashes
        if (path.startsWith('/')) {
            path = path.substring(1);
        }

        // 4. Return 'Raiz' if empty, otherwise the cleaned path
        return path || t('components.common.searchResultsPanel.root');
    }
    return null; // Default if no logical_path or other unhandled cases
};


const getItemColor = (item) => {
     // Tags hidden for now
     // Returns color for icon - using a standard grey/secondary color
     return 'currentColor';
}

const getItemIconColor = (item) => {
    const color = getItemColor(item);
    return color;
}

</script>

<template>
    <v-card
        class="search-results-panel mp-sensitive"
        elevation="4"
        :style="{ maxHeight: maxHeight, overflowY: 'auto' }"
    >
        <v-list density="compact" lines="two">
            <!-- Add :data-index="index" for scrolling -->
            <v-list-item
                v-for="(item, index) in results"
                :key="item.type + '-' + item.id"
                @click="selectItem(item)"
                class="search-result-item"
                :class="{ 'highlighted-item': index === highlightedIndex }"
                :data-index="index"
                :ripple="false"
            >
                 <template v-slot:prepend>
                    <component
                        v-if="getItemIconComponent(item)"
                        :is="getItemIconComponent(item)"
                        :size="18"
                        :color="getItemColor(item)"
                        class="search-result-icon"
                        stroke-width="1.5"
                    />
                 </template>

                <v-list-item-title class="item-title" v-html="highlightedName(item.name)"></v-list-item-title>
                <!-- Use the updated getItemPath function -->
                <v-list-item-subtitle v-if="getItemPath(item)" class="item-path">
                    {{ getItemPath(item) }}
                </v-list-item-subtitle>
                 <!-- Tags hidden for now -->
                 <v-list-item-subtitle v-else class="item-path text-grey-lighten-1">
                    {{ t('components.common.searchResultsPanel.pathUnavailable') }}
                </v-list-item-subtitle>

            </v-list-item>
        </v-list>
        
        <!-- Ver mais button -->
        <div v-if="hasMoreResults" class="show-more-container">
            <button class="show-more-btn" @click="emit('show-more')">
                <Plus :size="16" class="show-more-icon" />
                <span class="show-more-text">{{ t('components.common.searchResultsPanel.showMore') }}</span>
            </button>
        </div>

        <div v-if="results.length === 0 && searchTerm.trim()" class="pa-4 text-center text-caption text-grey-darken-1">
             {{ t('components.common.searchResultsPanel.noResults', { term: searchTerm.trim() }) }}
         </div>
    </v-card>
</template>

<style scoped>
.search-results-panel {
    /* Positioning is handled by the parent container */
    z-index: inherit; /* Inherit z-index from the positioning container */
    background-color: var(--color-surface);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    width: 100%;
}
.search-result-item {
    cursor: pointer;
    border-bottom: 1px solid var(--color-background-light);
    transition: background-color 0.1s ease-in-out;
}

.search-result-item :deep(.v-list-item__prepend) {
    margin-inline-end: 10px !important;
    width: auto;
}

.search-result-icon {
    flex-shrink: 0;
}
.search-result-item:last-child {
    border-bottom: none;
}
.search-result-item:hover {
    background-color: var(--color-background-light); /* Lighter hover */
}
/* Style for keyboard highlighted item */
.search-result-item.highlighted-item {
    background-color: var(--color-primary-opacity); /* Light primary background */
    border-color: var(--color-border-default); /* Border color */
}
.item-title {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--color-text-primary);
}
.item-path {
    font-size: 0.75rem;
    color: var(--color-text-secondary);
    padding-top: 2px;
}
/* Style for highlighted text using <mark> */
:deep(.search-highlight) {
    text-decoration: underline;
    text-decoration-thickness: 1.5px;
    background-color: transparent; /* Use transparent background */
    color: inherit; /* Keep original text color */
    font-weight: 600;
    padding: 0; /* Remove default padding from mark */
}

/* Show More Button */
.show-more-container {
    padding: 8px;
    border-top: 1px solid var(--color-background-light);
}

.show-more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: "Noto Sans", sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-secondary);
}

.show-more-btn:hover {
    background-color: var(--color-background-light);
    color: var(--color-text-primary);
}

.show-more-icon {
    flex-shrink: 0;
    color: inherit;
}

.show-more-text {
    font-size: 14px;
    font-weight: 500;
    color: inherit;
}
</style>
